/**
 * AcceptModal Component
 *
 * Modal for accepting a booking request.
 */

import React from 'react';

interface AcceptModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAccept: () => Promise<void>;
  isLoading: boolean;
  booking: {
    guestName: string;
    listingTitle: string;
    checkIn: Date;
    checkOut: Date;
    nights: number;
    hostPayout: number;
  };
}

export const AcceptModal: React.FC<AcceptModalProps> = ({
  isOpen,
  onClose,
  onAccept,
  isLoading,
  booking,
}) => {
  if (!isOpen) return null;

  const handleAccept = async () => {
    await onAccept();
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl max-w-md w-full p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Accept Booking</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <CloseIcon className="w-6 h-6" />
          </button>
        </div>

        <div className="mb-6">
          <div className="bg-green-50 rounded-xl p-4 mb-4">
            <div className="flex items-center gap-3">
              <CheckCircleIcon className="w-8 h-8 text-green-500" />
              <div>
                <p className="font-medium text-green-800">Confirm this booking?</p>
                <p className="text-sm text-green-600">
                  The guest will be notified immediately.
                </p>
              </div>
            </div>
          </div>

          <div className="space-y-3 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-500">Guest</span>
              <span className="font-medium text-gray-900">{booking.guestName}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Property</span>
              <span className="font-medium text-gray-900 text-right max-w-[60%] truncate">
                {booking.listingTitle}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Dates</span>
              <span className="font-medium text-gray-900">
                {booking.checkIn.toLocaleDateString('en-US', {
                  month: 'short',
                  day: 'numeric',
                })}{' '}
                -{' '}
                {booking.checkOut.toLocaleDateString('en-US', {
                  month: 'short',
                  day: 'numeric',
                  year: 'numeric',
                })}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Duration</span>
              <span className="font-medium text-gray-900">
                {booking.nights} {booking.nights === 1 ? 'night' : 'nights'}
              </span>
            </div>
            <div className="flex justify-between pt-3 border-t">
              <span className="text-gray-700 font-medium">Your payout</span>
              <span className="font-semibold text-green-600 text-lg">
                ${booking.hostPayout}
              </span>
            </div>
          </div>
        </div>

        <div className="flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 py-3 border border-gray-300 rounded-xl font-medium text-gray-700 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={handleAccept}
            disabled={isLoading}
            className="flex-1 py-3 bg-green-600 text-white rounded-xl font-medium hover:bg-green-700 disabled:opacity-50"
          >
            {isLoading ? 'Accepting...' : 'Accept Booking'}
          </button>
        </div>

        <p className="text-xs text-gray-500 mt-4 text-center">
          By accepting, you agree to welcome this guest and provide the accommodation as described.
        </p>
      </div>
    </div>
  );
};

// Icons
const CloseIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
  </svg>
);

const CheckCircleIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 24 24">
    <path fillRule="evenodd" d="M2.25 12c0-5.385 4.365-9.75 9.75-9.75s9.75 4.365 9.75 9.75-4.365 9.75-9.75 9.75S2.25 17.385 2.25 12zm13.36-1.814a.75.75 0 10-1.22-.872l-3.236 4.53L9.53 12.22a.75.75 0 00-1.06 1.06l2.25 2.25a.75.75 0 001.14-.094l3.75-5.25z" clipRule="evenodd" />
  </svg>
);

export default AcceptModal;
